import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import {
  MarketingAudience,
  emptyAudienceFilter,
  parseAudienceFilterGroup,
  parseReplyToUserIds,
} from "../leads/marketingCampaign";

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

export const marketingAudienceConverter: FirestoreDataConverter<MarketingAudience> = {
  toFirestore(audience: MarketingAudience) {
    const { id: _id, ...rest } = audience;
    const createdAt =
      rest.createdAt instanceof Date && !Number.isNaN(rest.createdAt.getTime())
        ? rest.createdAt
        : new Date();
    const updatedAt =
      rest.updatedAt instanceof Date && !Number.isNaN(rest.updatedAt.getTime())
        ? rest.updatedAt
        : createdAt;
    return {
      name: rest.name?.trim() ?? "",
      filter: parseAudienceFilterGroup(rest.filter),
      includedContactIds: parseReplyToUserIds(rest.includedContactIds),
      excludedContactIds: parseReplyToUserIds(rest.excludedContactIds),
      createdByUserId: rest.createdByUserId ?? "",
      createdAt: serializeDate(createdAt),
      updatedAt: serializeDate(updatedAt),
    };
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): MarketingAudience {
    const data = snapshot.data(options) ?? {};
    const createdAt = convertTimestamp(data.createdAt) || new Date();
    const includedContactIds = parseReplyToUserIds(data.includedContactIds);
    const excludedContactIds = parseReplyToUserIds(data.excludedContactIds);
    return {
      id: snapshot.id,
      name: typeof data.name === "string" ? data.name : "",
      filter: data.filter
        ? parseAudienceFilterGroup(data.filter)
        : emptyAudienceFilter(),
      ...(includedContactIds.length > 0 ? { includedContactIds } : {}),
      ...(excludedContactIds.length > 0 ? { excludedContactIds } : {}),
      createdByUserId:
        typeof data.createdByUserId === "string" ? data.createdByUserId : "",
      createdAt,
      updatedAt: convertTimestamp(data.updatedAt) || createdAt,
    };
  },
};
